import { useMemo } from 'react';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useProjectCustomFieldsAsFilterableFields } from './use-project-custom-fields-as-filterable-fields';
import { useProjectNativeFieldsLite } from './use-project-native-fields-as-filterable-fields';

/**
 * Lightweight version that doesn't call /members on page load.
 *
 * Uses useProjectNativeFieldsLite which:
 * - Returns empty metadata for user fields (teamMembers, projectOwner)
 * - Filters use fallback mechanisms (useFilterUserOptions) to fetch on-demand
 * - No upfront API calls for user data
 *
 * Safe for dashboards, grids, sheets, reports, and any filter view
 * where user options are resolved on-demand when the filter is opened.
 *
 * DO NOT use for:
 * - Views requiring upfront RBAC filtering (peopleIHaveAccessTo)
 */
export const useProjectFieldsAsFilterableFieldsLite = ({
  includeCurrentPeriodDetailsFilterFields,
}: {
  includeCurrentPeriodDetailsFilterFields?: boolean;
}): {
  loading: boolean;
  fields: FilterableField[];
} => {
  const { loading: customFieldsLoading, fields: customFields } =
    useProjectCustomFieldsAsFilterableFields();
  const { loading: nativeFieldsLoading, fields: nativeFields } =
    useProjectNativeFieldsLite({
      includeCurrentPeriodDetailsFilterFields,
    });

  return useMemo(
    () => ({
      loading: customFieldsLoading || nativeFieldsLoading,
      fields: nativeFields.concat(customFields),
    }),
    [customFields, customFieldsLoading, nativeFields, nativeFieldsLoading],
  );
};
